import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { DcuToMtuCommunicationActions } from './dcu-to-mtu-communication.actions';
import { initialFilters, initialState } from './dcu-to-mtu-communication.state';
import {
  AsyncRequestStatus,
  requestStatusFailure,
  requestStatusInProgress,
  requestStatusSuccess
} from '@portal/shared/store/request-status';
import { DATE_RANGE_FILTER, DCU_COVERAGE_FILTER, RELIABILITY_FILTER } from '../dcu-to-mtu-communication.constants';
import { DcuToMtuCommunicationFilterName } from '../dcu-to-mtu-communication.types';
import { GroupStatus } from '../../../administration/report-groups/report-groups.types';
import { CoverageStatistics, MultiselectItem } from '@ui/aos-lib';
import { Calc } from '../../../../shared/utils/calc';

const dcuMtuCommunicationReducer = createReducer(
  initialState,

  on(DcuToMtuCommunicationActions.fetchTableData, state => ({
    ...state,
    tableDataRequest: requestStatusInProgress()
  })),
  on(DcuToMtuCommunicationActions.fetchTableDataSuccess, (state, { data }) => ({
    ...state,
    tableData: data,
    tableDataRequest: requestStatusSuccess()
  })),
  on(DcuToMtuCommunicationActions.fetchTableDataFailure, (state, { error }) => ({
    ...state,
    tableDataRequest: requestStatusFailure(error)
  })),

  on(DcuToMtuCommunicationActions.fetchCoverageStatistics, state => ({
    ...state,
    statisticsRequest: requestStatusInProgress()
  })),
  on(DcuToMtuCommunicationActions.fetchCoverageStatisticsSuccess, (state, { data }) => ({
    ...state,
    statistics: data,
    statisticsRequest: requestStatusSuccess()
  })),
  on(DcuToMtuCommunicationActions.fetchCoverageStatisticsFailure, (state, { error }) => ({
    ...state,
    statisticsRequest: requestStatusFailure(error)
  })),

  on(DcuToMtuCommunicationActions.fetchReportGroupFailure, (state, { error }) => ({
    ...state,
    filtersRequest: requestStatusFailure(error)
  })),

  on(
    DcuToMtuCommunicationActions.getFiltersAndInitFiltersStateSuccess,
    (state, { data, filtersState, restoredTake }) => {
      const reportGroups = data.map(({ groupName, groupUID, groupStatus }) => ({
        text: groupName,
        value: groupUID,
        isDisabled: groupStatus === GroupStatus.Calculating
      }));
      const selectedReportGroupFilter = filtersState?.filters?.find(
        f => f.filterType === DcuToMtuCommunicationFilterName.ReportGroupUID
      );
      const selectedDcuCoverageFilter = filtersState?.filters?.find(
        f => f.filterType === DcuToMtuCommunicationFilterName.DcuCoverage
      );
      const selectedDateRangeFilter = filtersState?.filters?.find(
        f => f.filterType === DcuToMtuCommunicationFilterName.DateRange
      );
      const selectedReliabilityFilter = filtersState?.filters?.find(
        f => f.filterType === DcuToMtuCommunicationFilterName.Reliability
      );
      const matchedReportGroupItems = selectedReportGroupFilter
        ? reportGroups.filter(rg => selectedReportGroupFilter.filteredValues.includes(rg.value))
        : [];
      const selectedReportGroupFilterItems: MultiselectItem[] =
        selectedReportGroupFilter?.filteredValues.length && !matchedReportGroupItems.length
          ? reportGroups
          : matchedReportGroupItems;
      const selectedDcuCoverageFilterItems = selectedDcuCoverageFilter
        ? state.dcuCoverageFilter.items.filter(item =>
            selectedDcuCoverageFilter.filteredValues.includes(item.value as string)
          )
        : [];
      const selectedDateRangeFilterItems = selectedDateRangeFilter
        ? state.dateRangeFilter.items.filter(item =>
            selectedDateRangeFilter.filteredValues.includes(item.value as string)
          )
        : [];
      const selectedReliabilityFilterItems = selectedReliabilityFilter
        ? state.reliabilityFilter.items.filter(item =>
            selectedReliabilityFilter.filteredValues.includes(item.value as string)
          )
        : [];
      const reportGroupUidSet = new Set(reportGroups.map(rg => rg.value));
      const payloadFilters = filtersState?.filters?.length
        ? [
            ...state.payload.filters.filter(f => !filtersState.filters?.map(f => f.filterType).includes(f.filterType)),
            ...filtersState.filters
              .map(({ filterType, filteredValues }) => ({
                filterType,
                filteredValues:
                  filterType === DcuToMtuCommunicationFilterName.ReportGroupUID
                    ? filteredValues.filter(v => reportGroupUidSet.has(v))
                    : filteredValues
              }))
              .filter(
                ({ filterType, filteredValues }) =>
                  filterType !== DcuToMtuCommunicationFilterName.ReportGroupUID || filteredValues.length > 0
              )
          ]
        : null;

      return {
        ...state,
        reportGroupFilter: {
          ...state.reportGroupFilter,
          items: reportGroups,
          selected: selectedReportGroupFilter ? selectedReportGroupFilterItems : reportGroups
        },
        dcuCoverageFilter: {
          ...state.dcuCoverageFilter,
          ...(selectedDcuCoverageFilter && {
            selected: selectedDcuCoverageFilterItems
          })
        },
        dateRangeFilter: {
          ...state.dateRangeFilter,
          ...(selectedDateRangeFilter && {
            selected: selectedDateRangeFilterItems
          })
        },
        reliabilityFilter: {
          ...state.reliabilityFilter,
          ...(selectedReliabilityFilter && {
            selected: selectedReliabilityFilterItems
          })
        },
        payload: {
          ...state.payload,
          ...(restoredTake !== undefined && { take: restoredTake }),
          ...(filtersState?.filters.length && {
            filters: payloadFilters!
          })
        },
        filtersRequest: requestStatusSuccess(),
        isResetButtonVisible: JSON.stringify(payloadFilters) !== JSON.stringify(initialFilters)
      };
    }
  ),

  on(DcuToMtuCommunicationActions.updatePayloadByFilter, (state, { payload }) => ({
    ...state,
    payload
  })),
  on(DcuToMtuCommunicationActions.toggleResetButton, (state, { isResetVisible }) => ({
    ...state,
    isResetButtonVisible: isResetVisible
  })),
  on(DcuToMtuCommunicationActions.resetButton, state => ({
    ...state,
    payload: {
      ...initialState.payload,
      take: state.payload.take
    },
    isResetButtonVisible: false,
    dcuCoverageFilter: { ...DCU_COVERAGE_FILTER },
    dateRangeFilter: { ...DATE_RANGE_FILTER },
    reliabilityFilter: { ...RELIABILITY_FILTER },
    reportGroupFilter: {
      ...state.reportGroupFilter,
      selected: [...state.reportGroupFilter.items]
    },
    ...(state.payload.search && {
      payload: { ...initialState.payload, take: state.payload.take, search: state.payload.search }
    })
  })),
  on(DcuToMtuCommunicationActions.updateSearch, (state, { search }) => ({
    ...state,
    search
  })),
  on(DcuToMtuCommunicationActions.searchTypeChanged, (state, { isChanged }) => ({
    ...state,
    searchTypeChanged: isChanged
  })),
  on(DcuToMtuCommunicationActions.exportFile, state => ({
    ...state,
    exportRequest: requestStatusInProgress()
  })),
  on(DcuToMtuCommunicationActions.exportFileSuccess, state => ({
    ...state,
    exportRequest: requestStatusSuccess()
  })),
  on(DcuToMtuCommunicationActions.exportFileFailure, (state, { error }) => ({
    ...state,
    exportRequest: requestStatusFailure(error)
  })),
  on(DcuToMtuCommunicationActions.resetSearch, state => ({
    ...state,
    filtersRequest: requestStatusInProgress()
  })),
  on(DcuToMtuCommunicationActions.refresh, state => ({
    ...state,
    filtersRequest: requestStatusInProgress()
  }))
);

export const dcuMtuCommunicationFeature = createFeature({
  name: 'mtuDcuCommunication',
  reducer: dcuMtuCommunicationReducer,
  extraSelectors: ({ selectStatistics, selectMtuDcuCommunicationState, selectFiltersRequest }) => ({
    selectAverageDcuCount: createSelector(selectMtuDcuCommunicationState, state =>
      parseFloat(state.statistics.average.toString())
    ),
    selectCoverByStatistics: createSelector(selectStatistics, statistics => {
      const totalCount = statistics.counts.reduce((sum, item) => sum + item.count, 0);
      return DCU_COVERAGE_FILTER.items.map(item => {
        const foundCoverage = statistics.counts.find(({ coverage }) => coverage.toString() === item.value);
        return {
          label: item.text,
          colorType: CoverageStatistics[`CoveredBy${item.value}` as keyof typeof CoverageStatistics],
          percent: foundCoverage ? Math.round(Calc.percent(foundCoverage.count, totalCount) as number) : 0,
          value: foundCoverage ? foundCoverage.count : 0
        };
      });
    }),
    selectFiltersLoaded: createSelector(selectFiltersRequest, filtersRequest => {
      return filtersRequest.status === AsyncRequestStatus.Success;
    })
  })
});

export const {
  selectSearch,
  selectSearchTypeChanged,
  selectPayload,
  selectFiltersRequest,
  selectTableData,
  selectStatistics,
  selectIsResetButtonVisible,
  selectDcuCoverageFilter,
  selectDateRangeFilter,
  selectReliabilityFilter,
  selectReportGroupFilter,
  selectExportRequest,
  selectTableDataRequest,
  selectStatisticsRequest,
  selectMtuDcuCommunicationState,
  selectAverageDcuCount,
  selectCoverByStatistics,
  selectFiltersLoaded
} = dcuMtuCommunicationFeature;
